import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material';
import { FormControl } from '@angular/forms';
import { Store } from '@ngrx/store';
import { finalize, takeUntil } from 'rxjs/operators';
import { BehaviorSubject, ReplaySubject } from 'rxjs';

import { DeliveryNoteApiService } from 'projects/workspace/src/app/delivery-note/services/delivery-note-api.service';
import { ProductModel } from '../../../products';
import { CustomSearchFn } from '../../../rnpl-common/helpers';
import { ProductTypes } from '../../../products/product-types';
import { ProductUnitModel } from 'projects/workspace/src/app/shared/models';
import { ProductUnitApiService } from 'projects/workspace/src/app/shared/services';
import { AvailableProduct } from '../../modals-warehouse/pack-up-modal/models/available-product.model';
import { selectCompanyProfile } from 'projects/workspace/src/app/administration/store/selectors';
import { CompanyProfile } from 'projects/workspace/src/app/administration/models/company-profile.model';
import { AppState } from 'projects/workspace/src/app/store/state/app.state';

@Component({
  selector: 'rnpl-add-product-for-linked-modal',
  templateUrl: './add-product-for-linked-modal.component.html',
})
export class AddProductForLinkedModalComponent implements OnInit, OnDestroy {
  public companyProfile: CompanyProfile;
  public productIdControl: FormControl = new FormControl();
  public quantity: number;
  // public available: number;
  public selectedProductId: number;
  public selectedProductInvalid: boolean = false;
  public quantityInvalid: boolean = false;
  public allowFractionalValues: boolean = true;
  public addNotRelated: boolean = false;

  public customSearchFn = CustomSearchFn;

  private destroy$: ReplaySubject<any> = new ReplaySubject<any>(1);
  readonly productsList$: BehaviorSubject<any> = new BehaviorSubject<any>([]); // todo model
  readonly productsListUnlinked$: BehaviorSubject<any> = new BehaviorSubject<any>([]); // todo model
  readonly productsLoading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  readonly productsLoadingUnlinked$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  readonly btnToClearLoadingStatus$: BehaviorSubject<string> = new BehaviorSubject<string>(null);

  constructor(
    private store: Store<AppState>,
    private deliveryNoteApiService: DeliveryNoteApiService,
    private productUnitApiService: ProductUnitApiService,
    public dialogRef: MatDialogRef<AddProductForLinkedModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: {
      dnId: number,
      dnStatus: string,
      inventoryManagement: boolean,
    }
  ) {
    this.dialogRef.keydownEvents().subscribe(event => {
      if (event.key === 'Escape') {
        this.dialogRef.close();
      }
    });

    this.store.select(selectCompanyProfile)
      .pipe(takeUntil(this.destroy$))
      .subscribe((companyProfile: CompanyProfile) => this.companyProfile = companyProfile);
  }

  ngOnInit(): void {
    this.deliveryNoteApiService.getAvailableProductsForLinkedDeliveryNote(this.data.dnId)
      .pipe(
        finalize(() => this.productsLoading$.next(false)),
        takeUntil(this.destroy$)
      )
      .subscribe((products) => this.productsList$.next(this.prepareProductsList(products)));

    this.deliveryNoteApiService.getAvailableProductsForUnlinkedDeliveryNote(this.data.dnStatus)
      .pipe(
        finalize(() => this.productsLoadingUnlinked$.next(false)),
        takeUntil(this.destroy$)
      )
      .subscribe((products) => this.productsListUnlinked$.next(this.prepareProductsForUnlinkedList(products)));
  }

  public prepareProductsList(products): ProductModel[] {
    return products.map(item => {
      return {
        ...item,
        searchLabel: `${item.name} ${item.type} ${item.netPrice || 'n/a'} ${item.available}`
      };
    });
  }

  public prepareProductsForUnlinkedList(products): AvailableProduct[] {
    return products.map(item => {
      return {
        ...item,
        searchLabel: `${item.name} ${item.type} ${item.netPrice || 'n/a'} ${item.available} ${item.description} ${item.runpleId}`
      };
    });
  }

  public increaseQuantity(): void {
    this.quantity = (this.quantity || 0) + 1;
    this.quantityInvalid = false;
  }

  public decreaseQuantity(): void {
    if (!this.quantity) { return; }
    this.quantity = this.quantity - 1;
    this.quantityInvalid = false;
  }

  public submit(): void {
    if (!this.selectedProductId && !this.addNotRelated) {
      this.selectedProductInvalid = true;
    }

    if (!this.quantity) {
      this.quantityInvalid = true;
    }

    if ((!this.selectedProductId && !this.addNotRelated) || (this.addNotRelated && !this.productIdControl.value) || !this.quantity) {
      return;
    }

    this.deliveryNoteApiService.addProductUnitsToLinkedDeliveryNote(
      this.data.dnId,
      !this.addNotRelated
        ? this.selectedProductId
        : this.productIdControl.value,
      this.quantity
    ).subscribe(() => this.dialogRef.close());
  }

  public selectProductHandler(product: any): void {
    if (!!product.open && product.open > 0) {
      this.quantity = product.open;
    }
    // this.available = product.available;

    this.productUnitApiService.getUnits$(product.type as ProductTypes)
      .pipe(takeUntil(this.destroy$))
      .subscribe((unitsList: ProductUnitModel[]) => {
        const allowFractionalValues = unitsList.find(u => u.name === product.unitType).allowFractionalValues;
        const isFractionalQuantity: boolean = !!(product.open % 1);
        this.allowFractionalValues = allowFractionalValues || isFractionalQuantity;
      });
  }

  public productSelectedLinked(): void {
    const selectedProduct = this.productsList$.getValue().find(p => p.id === this.productIdControl.value);

    this.productUnitApiService.getUnits$(selectedProduct.type as ProductTypes)
      .pipe(takeUntil(this.destroy$))
      .subscribe((unitsList: ProductUnitModel[]) => {
        const allowFractionalValues = unitsList.find(u => u.name === selectedProduct.unitType).allowFractionalValues;
        this.allowFractionalValues = allowFractionalValues;
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next(null);
    this.destroy$.complete();
  }

}
